import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { MiniGrid } from "./MiniGrid";
import { MarketClock } from "./MarketClock";
import { Modal } from "./Modal";
import { ErrorState, Money, MktButton, StatePill, Stat, Tag } from "./primitives";
import { fmtMicro, shortAddr, symbolFor } from "./mkt-format";
import { mktError } from "./mkt-errors";
import { placementCells } from "./ArtworkCard";
import { useArtwork, useAuctions, useMarketplaceConfig } from "./useMarketplace";
import { useSession } from "./useSession";
import { useArtworkBuy } from "./artwork-buy";
import { useCancelAuction, useSettleAuction } from "./auction-onchain";
import { SellArtworkModal } from "./SellArtworkModal";
import { goMarketplace, viewOnGrid, type MarketplaceRouteState } from "./mkt-routing";
import { useT } from "../../i18n";
import { artworkAssetKey, marketplaceApi, MarketplaceError, type Auction, type ArtworkHistoryEvent, type FixedPriceListing, type MarketplaceConfig } from "../../lib/marketplace-api";

type TFn = (path: string, vars?: Record<string, string | number>) => string;

function errText(t: TFn, e: unknown) {
  if (e instanceof MarketplaceError) return mktError(t, e.code, e.message);
  return mktError(t, undefined, e instanceof Error ? e.message : undefined);
}

export function ArtworkDetail({ artworkId, route }: { artworkId: string; route: MarketplaceRouteState }) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const detailQuery = useArtwork(artworkId);
  const auctionsQuery = useAuctions();
  const session = useSession();
  const [offerOpen, setOfferOpen] = useState(false);
  const [bidOpen, setBidOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);

  const data = detailQuery.data ?? null;
  const cells = useMemo(() => placementCells(data?.placement ?? null), [data]);
  const marketNow = auctionsQuery.data?.market_now ?? Math.floor(Date.now() / 1000);

  if (detailQuery.isError) {
    return (
      <div className="max-w-md mx-auto py-12">
        <ErrorState title={t("marketplace.detail.artworkNotFound")} detail={t("marketplace.detail.artworkNotFoundDetail")} testId="artwork-detail-error"
          onRetry={() => goMarketplace("artworks", route.query)} />
      </div>
    );
  }
  if (!data) {
    return <div data-testid="artwork-detail-loading" className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">{t("marketplace.detail.loadingArtwork")}</div>;
  }

  const { artwork, placement, auction, offers } = data;
  const fixed = data.fixed_price;
  const history = data.history ?? [];
  const isOwner = !!session.wallet && session.wallet === artwork.owner.toLowerCase();
  const sellTarget = {
    artwork_id: artworkId, title: artwork.title, royalty_percent: artwork.royalty_percent,
    footprint: artwork.footprint, width: artwork.width, height: artwork.height,
  };
  const refetchAll = () => { detailQuery.refetch(); auctionsQuery.refetch(); };

  return (
    <div data-testid="artwork-detail" data-artwork-id={artworkId} className="max-w-5xl mx-auto">
      <button
        type="button"
        data-testid="artwork-detail-back"
        onClick={() => goMarketplace("artworks", route.query)}
        className="inline-flex items-center gap-2 mb-6 font-mono text-[10.5px] tracking-[0.16em] uppercase text-text-secondary hover:text-neon-teal transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden><path d="M9 6H3m0 0l3-3M3 6l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        {t("marketplace.detail.backToArtworks")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="neon-glass rounded-xl border border-border-divider overflow-hidden">
          <div className="relative aspect-square bg-elevated/40">
            <MiniGrid cells={cells} zoneClass="fill-neon-teal" testId="artwork-detail-minimap" />
            <div className="absolute top-3 left-3">
              {auction ? <Tag tone="coral">{t("marketplace.detail.auctionTag")}</Tag>
                : fixed ? <Tag tone="teal">{t("marketplace.artworkMkt.fixedPrice")}</Tag>
                : <Tag tone="teal">{t("marketplace.detail.openToOffers")}</Tag>}
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <h1 className="font-display text-3xl font-semibold text-text-primary tracking-tight">{artwork.title}</h1>
          <p className="mt-1 font-mono text-[11px] tracking-[0.14em] text-text-muted">
            {t("marketplace.detail.creatorOwner", { creator: shortAddr(artwork.creator), owner: shortAddr(artwork.owner) })}
          </p>
          {artwork.description && <p className="mt-4 font-mono text-[12px] leading-relaxed text-text-secondary max-w-md">{artwork.description}</p>}

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 neon-glass rounded-lg border border-border-divider p-4">
            <Stat label={t("marketplace.detail.size")}>{artwork.width}×{artwork.height}</Stat>
            <Stat label={t("marketplace.detail.cells")}>{artwork.footprint}</Stat>
            <Stat label={t("marketplace.detail.royalty")}>{artwork.royalty_percent}%</Stat>
            <Stat label={t("marketplace.detail.status")}>{artwork.status}</Stat>
          </div>

          {auction ? (
            <>
              <AuctionPanel auction={auction} marketNow={marketNow} config={config} isOwner={isOwner}
                onBid={() => setBidOpen(true)} />
              <AuctionActions auction={auction} marketNow={marketNow} isOwner={isOwner}
                config={config} onDone={refetchAll} />
            </>
          ) : fixed ? (
            <FixedPricePanel fixed={fixed} config={config} isOwner={isOwner} artworkId={artworkId}
              viewerWallet={session.wallet}
              onBuy={() => setBuyOpen(true)} onOffer={() => setOfferOpen(true)} onCancelled={refetchAll} />
          ) : isOwner ? (
            <div className="mt-6" data-testid="artwork-detail-sell">
              <MktButton variant="coral" testId="artwork-detail-sell-btn" onClick={() => setSellOpen(true)}>
                {t("marketplace.artworkMkt.sellArtwork")}
              </MktButton>
              <p className="mt-3 font-mono text-[10.5px] text-text-muted max-w-md leading-relaxed">{t("marketplace.artworkMkt.sellIntro")}</p>
            </div>
          ) : (
            config?.offers_enabled ? (
              <div className="mt-6 flex flex-wrap gap-3">
                <MktButton variant="primary" testId="artwork-detail-offer-btn" onClick={() => setOfferOpen(true)}>
                  {t("marketplace.detail.makeOffer")}
                </MktButton>
              </div>
            ) : null
          )}
          {placement && (
            <div className="mt-4">
              <MktButton variant="ghost" testId="artwork-detail-view-grid"
                onClick={() => viewOnGrid(placement.anchor_x + placement.width / 2, placement.anchor_y + placement.height / 2, Math.max(placement.width, placement.height) + 6, artworkId)}>
                {t("marketplace.detail.viewOnGrid")}
              </MktButton>
            </div>
          )}
        </div>
      </div>

      <div className="mt-12" data-testid="artwork-detail-offers">
        <h2 className="font-display text-lg font-semibold text-text-primary uppercase tracking-[0.02em] mb-4">{t("marketplace.detail.openOffers")}</h2>
        {offers.length === 0 ? (
          <p className="font-mono text-[11px] text-text-muted">{t("marketplace.detail.noOffersArtwork")}</p>
        ) : (
          <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
            {offers.map((o) => (
              <div key={o.offer_hash} data-testid="artwork-offer-row" className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="font-mono text-[11px] text-text-secondary">{shortAddr(o.offeror)}</span>
                <div className="flex items-center gap-3">
                  <Money amount={fmtMicro(o.amount_micro)} symbol={symbolFor(o.currency, config)} className="text-base text-text-primary" />
                  {isOwner && <OfferOwnerActions offerHash={o.offer_hash} onDone={() => detailQuery.refetch()} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ArtworkHistory history={history} config={config} />

      {offerOpen && (
        <OfferModal assetKey={artworkAssetKey(artworkId)} config={config} session={session}
          onClose={() => setOfferOpen(false)} onDone={() => detailQuery.refetch()} />
      )}
      {bidOpen && auction && (
        <BidModal auction={auction} config={config} session={session}
          onClose={() => setBidOpen(false)} onDone={() => { detailQuery.refetch(); auctionsQuery.refetch(); }} />
      )}
      {buyOpen && fixed && (
        <BuyArtworkModal artworkId={artworkId} title={artwork.title} fixed={fixed} config={config} session={session}
          onClose={() => setBuyOpen(false)} onDone={refetchAll} />
      )}
      {sellOpen && (
        <SellArtworkModal open target={sellTarget}
          onClose={() => setSellOpen(false)} onDone={refetchAll} />
      )}
    </div>
  );
}

function AuctionPanel({
  auction, marketNow, config, isOwner, onBid,
}: {
  auction: Auction; marketNow: number; config?: MarketplaceConfig | null; isOwner: boolean;
  onBid: () => void;
}) {
  const t = useT();
  const sym = symbolFor(auction.currency, config);
  const ended = auction.end_market_ts - marketNow <= 0;
  const hasBid = !!auction.highest_bidder;
  const current = hasBid ? auction.highest_bid_micro : auction.reserve_micro;
  const nextMin = hasBid ? auction.highest_bid_micro + auction.min_increment_micro : auction.reserve_micro;

  return (
    <div data-testid="auction-panel" className="mt-6 neon-glass rounded-xl border border-border-coral/40 p-5">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">{hasBid ? t("marketplace.detail.currentBid") : t("marketplace.detail.reserve")}</span>
        <MarketClock endMarketTs={auction.end_market_ts} marketNow={marketNow} antiSnipeWindow={auction.anti_snipe_window} testId="auction-panel-clock" />
      </div>
      <div className="mt-1.5"><Money amount={fmtMicro(current)} symbol={sym} className="text-3xl text-neon-teal" testId="auction-current" /></div>
      <div className="mt-1 font-mono text-[10.5px] text-text-muted">
        {hasBid ? t("marketplace.detail.leadingPrefix", { addr: shortAddr(auction.highest_bidder) }) : t("marketplace.detail.noBidsYet")}
        {t("marketplace.detail.minNext", { amt: fmtMicro(nextMin), sym })}
      </div>
      <div className="mt-4">
        <MktButton variant="coral" testId="auction-bid-btn" disabled={ended || isOwner || !config?.auctions_enabled} onClick={onBid}>
          {ended ? t("marketplace.detail.auctionEnded") : isOwner ? t("marketplace.detail.yourAuction") : t("marketplace.detail.placeBid")}
        </MktButton>
      </div>
    </div>
  );
}

function BidModal({
  auction, config, session, onClose, onDone,
}: {
  auction: Auction; config?: MarketplaceConfig | null; session: ReturnType<typeof useSession>;
  onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const sym = symbolFor(auction.currency, config);
  const hasBid = !!auction.highest_bidder;
  const nextMin = hasBid ? auction.highest_bid_micro + auction.min_increment_micro : auction.reserve_micro;
  const [amount, setAmount] = useState(String(nextMin / 1_000_000));
  const [error, setError] = useState<ReturnType<typeof errText> | null>(null);
  const [result, setResult] = useState<{ min: number; extend: boolean; signer: string } | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const micro = Math.round(Number(amount) * 1_000_000);
      return marketplaceApi.placeBid(auction.auction_id, { amount_micro: micro, nonce: Date.now() });
    },
    onSuccess: (r) => { setResult({ min: r.min_acceptable_micro, extend: r.anti_snipe_will_extend, signer: r.signer }); onDone(); },
    onError: (e) => setError(errText(t, e)),
  });

  const micro = Math.round(Number(amount) * 1_000_000);
  const canSubmit = session.isAuthenticated && micro >= nextMin && !mut.isPending && !result;

  return (
    <Modal open onClose={onClose} title={t("marketplace.detail.placeBidTitle")} subtitle={auction.artwork_title ?? t("marketplace.detail.auctionFallback")} testId="bid-modal"
      footer={
        result ? (
          <MktButton variant="primary" className="w-full" testId="bid-modal-done" onClick={onClose}>{t("marketplace.detail.done")}</MktButton>
        ) : (
          <div className="flex gap-3">
            <MktButton variant="ghost" className="flex-1" testId="bid-modal-cancel" onClick={onClose} disabled={mut.isPending}>{t("marketplace.detail.cancel")}</MktButton>
            <MktButton variant="coral" className="flex-1" testId="bid-modal-submit" onClick={() => { setError(null); mut.mutate(); }} disabled={!canSubmit}>
              {mut.isPending ? t("marketplace.detail.signing") : t("marketplace.detail.placeBid")}
            </MktButton>
          </div>
        )
      }
    >
      {!session.isAuthenticated && (
        <div data-testid="bid-modal-signin" className="mb-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.detail.connectSignIn")}</a>
        </div>
      )}
      <label className="block">
        <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.detail.yourBid", { sym })}</span>
        <input
          data-testid="bid-amount-input" type="number" inputMode="decimal" min="0" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full h-11 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-lg focus:outline-none focus:border-neon-teal/60"
        />
      </label>
      <p className="mt-2 font-mono text-[10.5px] text-text-muted">{t("marketplace.detail.minNextBid", { amt: fmtMicro(nextMin), sym })}</p>

      {result && (
        <div data-testid="bid-modal-ready" className="mt-5 rounded-lg border border-neon-teal/40 bg-neon-teal/10 p-4">
          <p className="font-display text-sm text-neon-teal uppercase tracking-[0.06em]">{t("marketplace.detail.bidPermitSigned")}</p>
          <p className="mt-2 font-mono text-[10.5px] text-text-secondary leading-relaxed">
            {t("marketplace.detail.bidPermitBody")}
            {result.extend && t("marketplace.detail.antiSnipe")}
          </p>
          <p className="mt-2 font-mono text-[10px] text-text-muted">{t("marketplace.detail.authorityMin", { addr: shortAddr(result.signer), amt: fmtMicro(result.min), sym })}</p>
        </div>
      )}
      {error && (
        <div data-testid="bid-modal-error" className="mt-5 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{error.title}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{error.hint}</p>
        </div>
      )}
    </Modal>
  );
}

function OfferModal({
  assetKey, config, session, onClose, onDone,
}: {
  assetKey: string; config?: MarketplaceConfig | null; session: ReturnType<typeof useSession>;
  onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const currencies = (config?.currencies ?? []).filter((c) => c.enabled && c.address);
  const [currency, setCurrency] = useState(currencies[0]?.address ?? "");
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState(24 * 3600);
  const [error, setError] = useState<ReturnType<typeof errText> | null>(null);
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: async () =>
      marketplaceApi.createOffer({
        asset_key: assetKey, currency: currency.toLowerCase(),
        amount_micro: Math.round(Number(amount) * 1_000_000),
        expiry_market_ts: Math.floor(Date.now() / 1000) + duration, nonce: Date.now(),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mkt", "artwork"] }); onDone(); onClose(); },
    onError: (e) => setError(errText(t, e)),
  });

  const micro = Math.round(Number(amount) * 1_000_000);
  const canSubmit = session.isAuthenticated && micro > 0 && !!currency && !mut.isPending;

  return (
    <Modal open onClose={onClose} title={t("marketplace.detail.makeOfferTitle")} subtitle={t("marketplace.detail.makeOfferSubtitle")} testId="artwork-offer-modal"
      footer={
        <div className="flex gap-3">
          <MktButton variant="ghost" className="flex-1" testId="artwork-offer-cancel" onClick={onClose} disabled={mut.isPending}>{t("marketplace.detail.cancel")}</MktButton>
          <MktButton variant="primary" className="flex-1" testId="artwork-offer-submit" onClick={() => { setError(null); mut.mutate(); }} disabled={!canSubmit}>
            {mut.isPending ? t("marketplace.detail.submitting") : t("marketplace.detail.submitOffer")}
          </MktButton>
        </div>
      }
    >
      {!session.isAuthenticated && (
        <div data-testid="artwork-offer-signin" className="mb-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.detail.connectSignIn")}</a>
        </div>
      )}
      <div className="space-y-4">
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.detail.amount")}</span>
          <div className="flex items-center gap-2">
            <input data-testid="artwork-offer-amount" type="number" inputMode="decimal" min="0" placeholder="0.00"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full h-10 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-base focus:outline-none focus:border-neon-teal/60" />
            <select data-testid="artwork-offer-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="h-10 px-2 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
              {currencies.map((c) => <option key={c.symbol} value={c.address ?? ""}>{c.symbol}</option>)}
            </select>
          </div>
        </label>
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.detail.expiresIn")}</span>
          <select data-testid="artwork-offer-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none">
            <option value={3600}>{t("marketplace.detail.dur1h")}</option>
            <option value={24 * 3600}>{t("marketplace.detail.dur24h")}</option>
            <option value={3 * 24 * 3600}>{t("marketplace.detail.dur3d")}</option>
            <option value={7 * 24 * 3600}>{t("marketplace.detail.dur7d")}</option>
          </select>
        </label>
      </div>
      {error && (
        <div data-testid="artwork-offer-error" className="mt-5 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{error.title}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{error.hint}</p>
        </div>
      )}
    </Modal>
  );
}

function OfferOwnerActions({ offerHash, onDone }: { offerHash: string; onDone: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [err, setErr] = useState<ReturnType<typeof errText> | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mkt", "artwork"] });
    qc.invalidateQueries({ queryKey: ["mkt", "artworks"] });
    onDone();
  };
  const accept = useMutation({
    mutationFn: () => marketplaceApi.acceptArtworkOffer(offerHash),
    onSuccess: invalidate,
    onError: (e) => setErr(errText(t, e)),
  });
  const decline = useMutation({
    mutationFn: () => marketplaceApi.declineOffer(offerHash),
    onSuccess: invalidate,
    onError: (e) => setErr(errText(t, e)),
  });
  const busy = accept.isPending || decline.isPending;
  return (
    <div className="flex items-center gap-2" data-testid="artwork-offer-owner-actions">
      <MktButton variant="primary" testId="artwork-offer-accept" onClick={() => { setErr(null); accept.mutate(); }} disabled={busy}>
        {accept.isPending ? "…" : "Accept"}
      </MktButton>
      <MktButton variant="ghost" testId="artwork-offer-decline" onClick={() => { setErr(null); decline.mutate(); }} disabled={busy}>
        {decline.isPending ? "…" : "Decline"}
      </MktButton>
      {err && <span data-testid="artwork-offer-action-error" className="font-mono text-[10px] text-neon-coral">{err.title}</span>}
    </div>
  );
}


function FixedPricePanel({
  fixed, config, isOwner, artworkId, viewerWallet, onBuy, onOffer, onCancelled,
}: {
  fixed: FixedPriceListing; config?: MarketplaceConfig | null; isOwner: boolean;
  artworkId: string; viewerWallet?: string | null;
  onBuy: () => void; onOffer: () => void; onCancelled: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const sym = symbolFor(fixed.currency, config);
  const binding = (fixed.buyer_binding || "").toLowerCase();
  const isBound = !!binding;
  const isBoundBuyer = isBound && !!viewerWallet && viewerWallet.toLowerCase() === binding;
  const cancel = useMutation({
    mutationFn: () => marketplaceApi.cancelArtworkListing(artworkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mkt", "artworks"] });
      qc.invalidateQueries({ queryKey: ["mkt", "artwork", artworkId] });
      qc.invalidateQueries({ queryKey: ["mkt", "mine"] });
      onCancelled();
    },
  });
  return (
    <div data-testid="artwork-fixed-panel" className="mt-6 neon-glass rounded-xl border border-neon-teal/30 p-5">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">{t("marketplace.artworkMkt.listedAt")}</span>
        {isOwner && <StatePill state="active" testId="artwork-fixed-status" />}
      </div>
      <div className="mt-1.5"><Money amount={fmtMicro(fixed.price_micro)} symbol={sym} className="text-3xl text-neon-teal" testId="artwork-fixed-price" /></div>
      <div className="mt-4 flex flex-wrap gap-3">
        {isBound ? (
          isBoundBuyer ? (
            <MktButton variant="primary" testId="artwork-complete-purchase" onClick={onBuy}>Complete your purchase</MktButton>
          ) : (
            <span data-testid="artwork-fixed-reserved" className="font-mono text-[10.5px] text-text-muted">Reserved for an accepted offer</span>
          )
        ) : isOwner ? (
          <MktButton variant="ghost" testId="artwork-cancel-listing" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            {cancel.isPending ? t("marketplace.artworkMkt.cancellingAuction") : t("marketplace.artworkMkt.cancelListing")}
          </MktButton>
        ) : (
          <>
            <MktButton variant="primary" testId="artwork-buy-btn" onClick={onBuy}>{t("marketplace.artworkMkt.buyNow")}</MktButton>
            {config?.offers_enabled && (
              <MktButton variant="ghost" testId="artwork-detail-offer-btn" onClick={onOffer}>{t("marketplace.detail.makeOffer")}</MktButton>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AuctionActions({
  auction, marketNow, isOwner, config, onDone,
}: {
  auction: Auction; marketNow: number; isOwner: boolean; config?: MarketplaceConfig | null; onDone: () => void;
}) {
  const t = useT();
  const settleHook = useSettleAuction();
  const cancelHook = useCancelAuction();
  const escrow = config?.auction_escrow ?? null;
  const ended = auction.end_market_ts - marketNow <= 0;
  const hasBid = !!auction.highest_bidder;

  useEffect(() => {
    if (settleHook.state.kind === "completed" || cancelHook.state.kind === "completed") onDone();
  }, [settleHook.state.kind, cancelHook.state.kind, onDone]);

  if (!escrow || !config) return null;
  const busy = (s: typeof settleHook.state) =>
    s.kind === "preparing" || s.kind === "awaiting_wallet" || s.kind === "submitting";

  const showSettle = ended && hasBid;
  const showCancel = isOwner && !hasBid && !ended;
  if (!showSettle && !showCancel) return null;

  return (
    <div data-testid="auction-actions" className="mt-3 flex flex-wrap gap-3">
      {showSettle && (
        <MktButton variant="primary" testId="auction-settle-btn"
          disabled={settleHook.isPending}
          onClick={() => settleHook.settle({ auctionId: auction.auction_id, escrow: escrow as `0x${string}`, chainId: config.chain_id })}>
          {busy(settleHook.state)
            ? (settleHook.state.kind === "awaiting_wallet" ? t("marketplace.artworkMkt.settleWalletConfirm") : t("marketplace.artworkMkt.settling"))
            : t("marketplace.artworkMkt.settleAuction")}
        </MktButton>
      )}
      {showCancel && (
        <MktButton variant="ghost" testId="auction-cancel-btn"
          disabled={cancelHook.isPending}
          onClick={() => cancelHook.cancel({ auctionId: auction.auction_id, escrow: escrow as `0x${string}`, chainId: config.chain_id })}>
          {busy(cancelHook.state) ? t("marketplace.artworkMkt.cancellingAuction") : t("marketplace.artworkMkt.cancelAuction")}
        </MktButton>
      )}
    </div>
  );
}

const BUY_PROGRESS: Record<string, string> = {
  preparing: "buyPreparing", checking_allowance: "buyCheckingAllowance",
  awaiting_approve_wallet: "buyApprove", approving: "buyApproving",
  awaiting_open_wallet: "buyOpenWallet", opening: "buyOpening",
  indexing_escrow: "buyIndexingEscrow", authorizing_release: "buyAuthorizing",
  awaiting_release_wallet: "buyReleaseWallet", releasing: "buyReleasing",
  indexing_payout: "buyIndexingPayout", finalizing: "buyFinalizing",
};

function BuyArtworkModal({
  artworkId, title, fixed, config, session, onClose, onDone,
}: {
  artworkId: string; title: string; fixed: FixedPriceListing; config?: MarketplaceConfig | null;
  session: ReturnType<typeof useSession>; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const { address } = useAccount();
  const { state, buy, isPending } = useArtworkBuy();
  const sym = symbolFor(fixed.currency, config);
  const escrow = config?.artwork_escrow ?? null;
  const walletMatch = !!address && !!session.wallet && address.toLowerCase() === session.wallet;
  const feePct = ((config?.grid_fee_bps ?? 250) / 100).toFixed(2);

  useEffect(() => {
    if (state.kind === "completed") onDone();
  }, [state.kind, onDone]);

  const start = () => {
    if (!escrow || !config || !session.wallet) return;
    buy({
      artworkId, escrow: escrow as `0x${string}`, usdc: fixed.currency as `0x${string}`,
      buyer: session.wallet as `0x${string}`, chainId: config.chain_id,
    });
  };

  const progressKey = BUY_PROGRESS[state.kind];
  const done = state.kind === "completed";
  const failed = state.kind === "error" || state.kind === "reverted" || state.kind === "rejected";
  const canBuy = session.isAuthenticated && walletMatch && !!escrow && !isPending && !done;

  return (
    <Modal open onClose={onClose} title={t("marketplace.artworkMkt.buyTitle", { title })}
      subtitle={t("marketplace.artworkMkt.buySubtitle")} testId="buy-artwork-modal"
      footer={
        done ? (
          <MktButton variant="primary" className="w-full" testId="buy-artwork-done" onClick={onClose}>{t("marketplace.artworkMkt.done")}</MktButton>
        ) : (
          <div className="flex gap-3">
            <MktButton variant="ghost" className="flex-1" testId="buy-artwork-cancel" onClick={onClose} disabled={isPending}>{t("marketplace.artworkMkt.cancel")}</MktButton>
            <MktButton variant="primary" className="flex-1" testId="buy-artwork-confirm" onClick={start} disabled={!canBuy}>
              {isPending ? t("marketplace.artworkMkt.buyPreparing") : t("marketplace.artworkMkt.confirmBuy")}
            </MktButton>
          </div>
        )
      }
    >
      <p className="font-mono text-[10.5px] text-text-muted leading-relaxed">{t("marketplace.artworkMkt.buyIntro")}</p>
      <div className="mt-4 grid grid-cols-3 gap-3 neon-glass rounded-lg border border-border-divider p-4">
        <Stat label={t("marketplace.artworkMkt.priceLabel")}>{fmtMicro(fixed.price_micro)} {sym}</Stat>
        <Stat label={t("marketplace.artworkMkt.gridFeeLabel")}>{feePct}%</Stat>
        <Stat label={t("marketplace.artworkMkt.royaltyLabel")}>{(fixed.royalty_bps / 100).toFixed(0)}%</Stat>
      </div>
      {!session.isAuthenticated && (
        <div data-testid="buy-artwork-signin" className="mt-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.artworkMkt.signInFirst")}</a>
        </div>
      )}
      {session.isAuthenticated && !walletMatch && (
        <p data-testid="buy-artwork-wallet" className="mt-4 font-mono text-[10.5px] text-state-warning">
          {t("marketplace.artworkMkt.connectBuyer", { addr: shortAddr(session.wallet) })}
        </p>
      )}
      {progressKey && (
        <div data-testid="buy-artwork-progress" className="mt-4 rounded-lg border border-neon-teal/40 bg-neon-teal/10 p-4">
          <p className="font-mono text-[11px] text-neon-teal">{t(`marketplace.artworkMkt.${progressKey}`)}</p>
        </div>
      )}
      {done && (
        <div data-testid="buy-artwork-complete" className="mt-4 rounded-lg border border-neon-teal/40 bg-neon-teal/10 p-4">
          <p className="font-display text-sm text-neon-teal">{t("marketplace.artworkMkt.buyComplete")}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{t("marketplace.artworkMkt.buyCompleteBody")}</p>
        </div>
      )}
      {failed && (
        <div data-testid="buy-artwork-error" className="mt-4 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{t("marketplace.artworkMkt.buyFailed")}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">
            {state.kind === "rejected" ? t("marketplace.artworkMkt.buyRejected")
              : state.kind === "reverted" ? t("marketplace.artworkMkt.buyReverted")
              : state.kind === "error" ? state.error : ""}
          </p>
        </div>
      )}
    </Modal>
  );
}

const HISTORY_LABEL: Record<string, string> = {
  SETTLEMENT_PREPARED: "histPrepared",
  SETTLEMENT_FINALIZED: "histFinalized",
  ORDER_FILLED: "histFinalized",
  AUCTION_SETTLED: "histAuctionSettled",
};

function ArtworkHistory({ history, config }: { history: ArtworkHistoryEvent[]; config?: MarketplaceConfig | null }) {
  const t = useT();
  const rows = history.filter((h) => HISTORY_LABEL[h.kind]);
  return (
    <div className="mt-12" data-testid="artwork-detail-history">
      <h2 className="font-display text-lg font-semibold text-text-primary uppercase tracking-[0.02em] mb-4">{t("marketplace.artworkMkt.historyTitle")}</h2>
      {rows.length === 0 ? (
        <p className="font-mono text-[11px] text-text-muted">{t("marketplace.artworkMkt.historyEmpty")}</p>
      ) : (
        <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
          {rows.map((h) => {
            const facts = h.facts || {};
            const amount = facts.amount_micro ?? facts.price_micro;
            const tx = typeof facts.tx_hash === "string" ? facts.tx_hash : null;
            return (
              <div key={h.seq} data-testid="artwork-history-row" className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="font-display text-sm text-text-primary">{t(`marketplace.artworkMkt.${HISTORY_LABEL[h.kind]}`)}</span>
                  {typeof facts.new_owner === "string" && (
                    <span className="font-mono text-[10px] text-text-muted">{shortAddr(facts.new_owner as string)}</span>
                  )}
                  {typeof facts.winner === "string" && (
                    <span className="font-mono text-[10px] text-text-muted">{shortAddr(facts.winner as string)}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {typeof amount === "number" && (
                    <Money amount={fmtMicro(amount)} symbol={symbolFor(null, config)} className="text-sm text-neon-teal" />
                  )}
                  {tx && (
                    <a href={`https://sepolia.basescan.org/tx/${tx}`} target="_blank" rel="noreferrer"
                      className="font-mono text-[10px] text-neon-teal hover:underline">{t("marketplace.artworkMkt.viewTx")}</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

